import { supabase } from '../../../shared/lib/supabaseClient';
import { PASSING_GRADE } from '../../../shared/constant/grading';

// A passing grade this close to the passing mark counts as nearly failing.
export const NEAR_FAILING_MARGIN = 3;
// A student is flagged for absenteeism once this share of their recorded days (present + late +
// absent, the same days the attendance average counts) are absences, over at least this many days.
export const ABSENT_RATE_THRESHOLD = 0.2;
export const MIN_RECORDED_DAYS = 5;

const EMPTY = { failing: [], nearlyFailing: [], absenteeism: [] };

// Students in the teacher's subjects who need a closer look: failing, nearly failing, or often
// absent. `subjects` is [{ id, name }]. A grade is a student's latest graded term in a subject.
// Each entry is { student, ... } with the student's { id, lrn, firstname, middlename, lastname, grade_level, section }.
export async function getAttentionList(subjects) {
  if (subjects.length === 0) return EMPTY;

  const subjectIds = subjects.map((s) => s.id);
  const subjectNames = Object.fromEntries(subjects.map((s) => [s.id, s.name]));

  const [gradeResult, attendanceResult] = await Promise.all([
    supabase
      .from('subject_grades')
      .select('student_id, subject_id, term, term_grade')
      .in('subject_id', subjectIds)
      .not('term_grade', 'is', null),
    supabase
      .from('student_attendance_summary')
      .select('student_id, present, late, absent')
      .in('subject_id', subjectIds),
  ]);
  if (gradeResult.error) throw gradeResult.error;
  if (attendanceResult.error) throw attendanceResult.error;

  // The latest graded term for each student in each subject.
  const latestGrades = new Map();
  gradeResult.data.forEach((row) => {
    const key = `${row.student_id}|${row.subject_id}`;
    const current = latestGrades.get(key);
    if (!current || row.term > current.term) latestGrades.set(key, row);
  });

  const failing = new Map();
  const nearlyFailing = new Map();
  latestGrades.forEach((row) => {
    const grade = Number(row.term_grade);
    const bucket =
      grade < PASSING_GRADE ? failing : grade < PASSING_GRADE + NEAR_FAILING_MARGIN ? nearlyFailing : null;
    if (!bucket) return;
    if (!bucket.has(row.student_id)) bucket.set(row.student_id, []);
    bucket.get(row.student_id).push({ subjectId: row.subject_id, subject: subjectNames[row.subject_id] ?? 'Subject', term: row.term, grade });
  });

  const attendance = new Map();
  attendanceResult.data.forEach((row) => {
    const totals = attendance.get(row.student_id) ?? { absent: 0, counted: 0 };
    totals.absent += Number(row.absent);
    totals.counted += Number(row.present) + Number(row.late) + Number(row.absent);
    attendance.set(row.student_id, totals);
  });
  const absent = [...attendance.entries()]
    .filter(([, t]) => t.counted >= MIN_RECORDED_DAYS && t.absent / t.counted >= ABSENT_RATE_THRESHOLD)
    .map(([studentId, t]) => ({ studentId, absent: t.absent, counted: t.counted, rate: t.absent / t.counted }));

  const studentIds = [...new Set([...failing.keys(), ...nearlyFailing.keys(), ...absent.map((a) => a.studentId)])];
  if (studentIds.length === 0) return EMPTY;

  const { data: students, error } = await supabase
    .from('students')
    .select('id, lrn, firstname, middlename, lastname, grade_level, section')
    .in('id', studentIds);
  if (error) throw error;
  const studentById = Object.fromEntries(students.map((s) => [s.id, s]));

  const withGrades = (bucket) =>
    [...bucket.entries()]
      .filter(([studentId]) => studentById[studentId])
      .map(([studentId, grades]) => ({
        student: studentById[studentId],
        grades: grades.sort((a, b) => a.grade - b.grade),
      }))
      .sort((a, b) => a.grades[0].grade - b.grades[0].grade);

  return {
    failing: withGrades(failing),
    nearlyFailing: withGrades(nearlyFailing),
    absenteeism: absent
      .filter((a) => studentById[a.studentId])
      .map((a) => ({ student: studentById[a.studentId], absent: a.absent, counted: a.counted, rate: a.rate }))
      .sort((a, b) => b.rate - a.rate),
  };
}

// ---------- Marked as addressed ----------
// A teacher marks a flagged student as addressed (attention_reviews in schema.sql). The flag's
// figures are saved with the mark, and it stops counting once things get worse: a failing grade in a
// later term, a lower grade, or more absences.

// Tells the notification bell to recount after a mark changes.
export const ATTENTION_REVIEWED_EVENT = 'pawigrade:attention-reviewed';

const reviewKey = (reason, studentId, subjectId) => `${reason}|${studentId}|${subjectId ?? ''}`;

// The signed-in account's own marks (RLS limits it to them).
export async function getAttentionReviews() {
  const { data, error } = await supabase
    .from('attention_reviews')
    .select('id, student_id, reason, subject_id, term, grade, absent');
  if (error) throw error;
  return data;
}

// `flag` is { reason, studentId, subjectId?, term?, grade?, absent? }. Replaces any
// earlier mark for the same student, reason and subject (e.g. one that no longer counts).
export async function markAddressed(flag) {
  let remove = supabase
    .from('attention_reviews')
    .delete()
    .eq('student_id', flag.studentId)
    .eq('reason', flag.reason);
  remove = flag.subjectId ? remove.eq('subject_id', flag.subjectId) : remove.is('subject_id', null);
  const { error: removeError } = await remove;
  if (removeError) throw removeError;

  const { data, error } = await supabase
    .from('attention_reviews')
    .insert({
      student_id: flag.studentId,
      reason: flag.reason,
      subject_id: flag.subjectId ?? null,
      term: flag.term ?? null,
      grade: flag.grade ?? null,
      absent: flag.absent ?? null,
    })
    .select('id, student_id, reason, subject_id, term, grade, absent')
    .single();
  if (error) throw error;
  window.dispatchEvent(new Event(ATTENTION_REVIEWED_EVENT));
  return data;
}

export async function undoAddressed(reviewId) {
  const { error } = await supabase.from('attention_reviews').delete().eq('id', reviewId);
  if (error) throw error;
  window.dispatchEvent(new Event(ATTENTION_REVIEWED_EVENT));
}

// Whether a mark still covers the flag as it stands now. Also used by the student dashboard.
export function isStillAddressed(review, flag) {
  if (review.reason === 'absenteeism') return flag.absent <= Number(review.absent ?? 0);
  return flag.term <= Number(review.term ?? 0) && flag.grade >= Number(review.grade ?? Infinity);
}

// Adds `review` (the mark that still covers it, or null) to each flag in a teacher's list: on each
// failing grade of an entry (one per subject) and on each absence entry. Near-failing grades aren't
// marked.
export function applyReviews(attention, reviews) {
  if (!attention) return attention;
  const byKey = new Map(reviews.map((r) => [reviewKey(r.reason, r.student_id, r.subject_id), r]));
  const find = (reason, studentId, subjectId, flag) => {
    const review = byKey.get(reviewKey(reason, studentId, subjectId));
    return review && isStillAddressed(review, flag) ? review : null;
  };

  return {
    ...attention,
    failing: attention.failing.map((entry) => ({
      ...entry,
      grades: entry.grades.map((g) => ({ ...g, review: find('failing', entry.student.id, g.subjectId, g) })),
    })),
    absenteeism: attention.absenteeism.map((entry) => ({
      ...entry,
      review: find('absenteeism', entry.student.id, null, entry),
    })),
  };
}

// After applyReviews: the entries not (fully) marked as addressed, for the notification bell.
export function unaddressed(attention) {
  return {
    failing: attention.failing.filter((entry) => entry.grades.some((g) => !g.review)),
    absenteeism: attention.absenteeism.filter((entry) => !entry.review),
  };
}
